// src/components/ChangePassword.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Building2, Lock, AlertCircle, CheckCircle, Loader, ArrowLeft, Eye, EyeOff } from 'lucide-react';

const ChangePassword = ({ user }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [show, setShow] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const toggleShow = (field) => {
    setShow(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (formData.newPassword.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `/api/members/${user.member_number}/change_password/`,
        {
          current_password: formData.currentPassword,
          new_password: formData.newPassword,
          confirm_password: formData.confirmPassword
        }
      );

      if (response.data.success) {
        setSuccess(true);
        setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setError(response.data.message || 'Failed to change password.');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors || 'Failed to change password. Please try again.';
      setError(typeof msg === 'object' ? JSON.stringify(msg) : msg);
    } finally {
      setLoading(false);
    }
  };

  const PasswordField = ({ id, label, hint }) => (
    <div className="form-group">
      <label htmlFor={id}>{label}</label>
      <div className="password-input-wrapper">
        <input
          type={show[id] ? 'text' : 'password'}
          id={id}
          name={id}
          value={formData[id]}
          onChange={handleChange}
          placeholder={hint}
          required
          disabled={loading}
        />
        <button
          type="button"
          className="password-toggle"
          onClick={() => toggleShow(id)}
          tabIndex={-1}
        >
          {show[id] ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <Building2 className="logo-icon" />
          <h1>Change Password</h1>
          <p>Update your account password</p>
        </div>

        {success ? (
          <div className="success-state">
            <CheckCircle size={48} className="success-icon" />
            <h3>Password Changed</h3>
            <p>Your password has been updated successfully.</p>
            <button onClick={() => navigate('/dashboard')} className="login-button">
              <ArrowLeft size={18} />
              <span>Back to Dashboard</span>
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <div className="password-input-wrapper">
                <input
                  type={show.currentPassword ? 'text' : 'password'}
                  id="currentPassword"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  placeholder="Your current password"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => toggleShow('currentPassword')}
                  tabIndex={-1}
                >
                  {show.currentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="field-hint">If you haven't changed it, your current password is your email address.</p>
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <div className="password-input-wrapper">
                <input
                  type={show.newPassword ? 'text' : 'password'}
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  placeholder="At least 6 characters"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => toggleShow('newPassword')}
                  tabIndex={-1}
                >
                  {show.newPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <div className="password-input-wrapper">
                <input
                  type={show.confirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Repeat new password"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => toggleShow('confirmPassword')}
                  tabIndex={-1}
                >
                  {show.confirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="error-message">
                <AlertCircle className="error-icon" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !formData.currentPassword || !formData.newPassword || !formData.confirmPassword}
              className="login-button"
            >
              {loading ? (
                <>
                  <Loader className="spinner" />
                  <span>Changing Password...</span>
                </>
              ) : (
                <>
                  <Lock size={18} />
                  <span>Change Password</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="back-link"
              disabled={loading}
            >
              <ArrowLeft size={16} />
              <span>Back to Dashboard</span>
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChangePassword;
